import { FunctionComponent } from 'react'

import styled, { css } from 'styled-components'

import { TQuestionData } from '@utils/chatbot/types'
import translate from '@i18n'
import SecondaryCTAButton from '@components/Buttons/SecondaryCTAButton'

import Loader from '../Loader'
import SuggestedQuestion from './SuggestedQuestion'

type SuggestedQuestionsListProps = {
  isLoading: boolean
  questions?: TQuestionData[]
  onQuestionClick: (question: TQuestionData) => void
  hasError?: boolean
  onRefetch: () => void
}

const StyledSuggestedQuestionsList = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing8};
  `}
`

const SuggestedQuestionTitle = styled.div`
  ${({
    theme: {
      colors: { strokeBrand },
      spacing: { spacing8 },
      typography: {
        headings: { small: smallHeadingCSS },
      },
    },
  }) => css`
    ${smallHeadingCSS}
    text-transform: uppercase;
    border-left: 2px solid ${strokeBrand};
    padding-left: ${spacing8};
    margin-bottom: ${spacing8};
  `}
`

const ErrorMessage = styled.div`
  ${({
    theme: {
      colors: { fillWeak },
      spacing: { spacing16 },
    },
  }) => css`
    padding: ${spacing16};
    gap: ${spacing16};
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    background-color: ${fillWeak};
  `}
`

const ErrorMessageTitle = styled.div`
  ${({
    theme: {
      typography: {
        bodycopy: {
          small1: { bundledCSS: small1BodycopyCSS },
        },
      },
    },
  }) => css`
    ${small1BodycopyCSS};
  `}
`
const ErrorMessageText = styled.div`
  ${({
    theme: {
      typography: {
        bodycopy: {
          small2: { bundledCSS: small2BodycopyCSS },
        },
      },
    },
  }) => css`
    ${small2BodycopyCSS};
  `}
`

const StyledSecondaryCTAButton = styled(SecondaryCTAButton)`
  width: 100%;
`

const SuggestedQuestionsList: FunctionComponent<
  SuggestedQuestionsListProps
> = ({ questions, onQuestionClick, isLoading, hasError, onRefetch }) => {
  return (
    <StyledSuggestedQuestionsList>
      <SuggestedQuestionTitle>
        {translate('aiSearch.suggestedQuestions.title')}
      </SuggestedQuestionTitle>
      {hasError ? (
        <ErrorMessage>
          <ErrorMessageTitle>
            {translate('network.error.title')}
          </ErrorMessageTitle>
          <ErrorMessageText>
            {translate('aiSearch.suggestedQuestions.errorMessage')}
          </ErrorMessageText>
          <StyledSecondaryCTAButton
            colorVariant="black"
            size="large"
            iconName="arrows-rotate"
            onClick={onRefetch}
          >
            {translate('network.error.reload')}
          </StyledSecondaryCTAButton>
        </ErrorMessage>
      ) : isLoading ? (
        <Loader />
      ) : questions?.length ? (
        questions?.map((question) => (
          <SuggestedQuestion
            key={question.id}
            isLoading={isLoading}
            question={question}
            onClick={onQuestionClick}
          />
        ))
      ) : null}
    </StyledSuggestedQuestionsList>
  )
}

export default SuggestedQuestionsList
